import "./PrivacyPolicyContent.css";

const SECTIONS = [
  { id: "infocollect", label: "1. What information do we collect?" },
  { id: "infouse", label: "2. How do we process your information?" },
  { id: "whoshare", label: "3. When and with whom do we share your personal information?" },
  { id: "cookies", label: "4. Do we use cookies and other tracking technologies?" },
  { id: "ai", label: "5. Do we offer artificial intelligence-based products?" },
  { id: "inforetain", label: "6. How long do we keep your information?" },
  { id: "infosafe", label: "7. How do we keep your information safe?" },
  { id: "infominors", label: "8. Do we collect information from minors?" },
  { id: "privacyrights", label: "9. What are your privacy rights?" },
  { id: "DNT", label: "10. Controls for Do-Not-Track features" },
  { id: "uslaws", label: "11. Do United States residents have specific privacy rights?" },
  { id: "policyupdates", label: "12. Do we make updates to this notice?" },
  { id: "contact", label: "13. How can you contact us about this notice?" },
  { id: "request", label: "14. How can you review, update, or delete the data we collect from you?" },
];

export function PrivacyPolicyContent() {
  return (
    <article className="booth-legal">
      <header className="booth-legal-header">
        <h1>Privacy policy</h1>
        <p className="booth-legal-subtitle">Last updated April 02, 2026</p>
      </header>

      <p>
        This Privacy Notice for The Booth ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"),
        describes how and why we might access, collect, store, use, and/or share ("<strong>process</strong>")
        your personal information when you use our services ("<strong>Services</strong>"), including when you:
      </p>

      <ul>
        <li>
          Visit our website at{" "}
          <a rel="noopener noreferrer" target="_blank" href="https://thebooth.gg">
            https://thebooth.gg
          </a>
          {" "}or any website of ours that links to this Privacy Notice
        </li>
        <li>
          Use The Booth. A subscription service that provides coverage for a user's fantasy sports league.
          For entertainment purposes only.
        </li>
        <li>Engage with us in other related ways, including any marketing or events</li>
      </ul>

      <p>
        <strong>Questions or concerns? </strong>
        Reading this Privacy Notice will help you understand your privacy rights and choices.
        We are responsible for making decisions about how your personal information is processed.
        If you do not agree with our policies and practices, please do not use our Services.
        If you still have any questions or concerns, please contact us at{" "}
        <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
          admin@thebooth.gg
        </a>
        .
      </p>

      <section>
        <h2>Summary of key points</h2>

        <p>
          <em>
            <strong>This summary provides key points from our Privacy Notice, but you can find out more details about
            any of these topics by clicking the link following each key point or by using our{" "}</strong>
          </em>
          <a href="#toc">
            <em><strong>table of contents</strong></em>
          </a>
          <em><strong>{" "}below to find the section you are looking for.</strong></em>
        </p>

        <p>
          <strong>What personal information do we process?</strong>{" "}
          When you visit, use, or navigate our Services, we may process personal information depending on how
          you interact with us and the Services, the choices you make, and the products and features you use.
          Learn more about{" "}
          <a href="#personalinfo">personal information you disclose to us</a>.
        </p>

        <p>
          <strong>Do we process any sensitive personal information?</strong>{" "}
          Some of the information may be considered "special" or "sensitive" in certain jurisdictions,
          for example your racial or ethnic origins, sexual orientation, and religious beliefs.
          We do not process sensitive personal information.
        </p>

        <p>
          <strong>Do we collect any information from third parties?</strong>{" "}
          We may collect information from public databases, marketing partners, social media platforms,
          and other outside sources. Learn more about{" "}
          <a href="#othersources">information collected from other sources</a>.
        </p>

        <p>
          <strong>How do we process your information?</strong>{" "}
          We process your information to provide, improve, and administer our Services, communicate with you,
          for security and fraud prevention, and to comply with law. We may also process your information for
          other purposes with your consent. We process your information only when we have a valid legal reason
          to do so. Learn more about{" "}
          <a href="#infouse">how we process your information</a>.
        </p>

        <p>
          <strong>In what situations and with which parties do we share personal information?</strong>{" "}
          We may share information in specific situations and with specific third parties. Learn more about{" "}
          <a href="#whoshare">when and with whom we share your personal information</a>.
        </p>

        <p>
          <strong>How do we keep your information safe?</strong>{" "}
          We have adequate organizational and technical processes and procedures in place to protect your
          personal information. However, no electronic transmission over the internet or information storage
          technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able to defeat our security and
          improperly collect, access, steal, or modify your information. Learn more about{" "}
          <a href="#infosafe">how we keep your information safe</a>.
        </p>

        <p>
          <strong>What are your rights?</strong>{" "}
          Depending on where you are located geographically, the applicable privacy law may mean you have
          certain rights regarding your personal information. Learn more about{" "}
          <a href="#privacyrights">your privacy rights</a>.
        </p>

        <p>
          <strong>How do you exercise your rights?</strong>{" "}
          The easiest way to exercise your rights is by visiting{" "}
          <a rel="noopener noreferrer" target="_blank" href="https://thebooth.gg/contactus">
            https://thebooth.gg/contactus
          </a>
          , or by contacting us. We will consider and act upon any request in accordance with applicable
          data protection laws.
        </p>

        <p>
          Want to learn more about what we do with any information we collect?{" "}
          <a href="#toc">Review the Privacy Notice in full</a>.
        </p>
      </section>

      <nav id="toc" className="booth-legal-toc" aria-label="Table of contents">
        <h2>Table of contents</h2>
        <ol>
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <section id="infocollect">
        <h2>1. What information do we collect?</h2>

        <h3 id="personalinfo">Personal information you disclose to us</h3>

        <p><em><strong>In Short:</strong> We collect personal information that you provide to us.</em></p>

        <p>
          We collect personal information that you voluntarily provide to us when you register on the Services,
          express an interest in obtaining information about us or our products and Services, when you participate
          in activities on the Services, or otherwise when you contact us.
        </p>

        <p>
          <strong>Personal Information Provided by You.</strong>{" "}
          The personal information that we collect depends on the context of your interactions with us and the
          Services, the choices you make, and the products and features you use. The personal information we
          collect may include the following:
        </p>

        <ul>
          <li>names</li>
          <li>email addresses</li>
          <li>passwords</li>
        </ul>

        <p id="sensitiveinfo">
          <strong>Sensitive Information.</strong> We do not process sensitive information.
        </p>

        <p>
          All personal information that you provide to us must be true, complete, and accurate, and you must
          notify us of any changes to such personal information.
        </p>

        <h3>Information automatically collected</h3>

        <p>
          <em><strong>In Short:</strong> Some information, such as your Internet Protocol (IP) address and/or browser and
          device characteristics, is collected automatically when you visit our Services.</em>
        </p>

        <p>
          We automatically collect certain information when you visit, use, or navigate the Services. This
          information does not reveal your specific identity (like your name or contact information) but may
          include device and usage information, such as your IP address, browser and device characteristics,
          operating system, language preferences, referring URLs, device name, country, location, information
          about how and when you use our Services, and other technical information. This information is primarily
          needed to maintain the security and operation of our Services, and for our internal analytics and
          reporting purposes.
        </p>

        <p>The information we collect includes:</p>

        <ul>
          <li>
            <em>Log and Usage Data.</em>{" "}
            Log and usage data is service-related, diagnostic, usage, and performance information our servers
            automatically collect when you access or use our Services and which we record in log files. Depending
            on how you interact with us, this log data may include your IP address, device information, browser
            type, and settings and information about your activity in the Services (such as the date/time stamps
            associated with your usage, pages and files viewed, searches, and other actions you take such as
            which features you use), device event information (such as system activity, error reports (sometimes
            called "crash dumps"), and hardware settings).
          </li>
          <li>
            <em>Device Data.</em>{" "}
            We collect device data such as information about your computer, phone, tablet, or other device you
            use to access the Services. Depending on the device used, this device data may include information
            such as your IP address (or proxy server), device and application identification numbers, location,
            browser type, hardware model, Internet service provider and/or mobile carrier, operating system,
            and system configuration information.
          </li>
          <li>
            <em>Location Data.</em>{" "}
            We collect location data such as information about your device's location, which can be either
            precise or imprecise. How much information we collect depends on the type and settings of the device
            you use to access the Services. For example, we may use GPS and other technologies to collect
            geolocation data that tells us your current location (based on your IP address). You can opt out of
            allowing us to collect this information either by refusing access to the information or by disabling
            your Location setting on your device. However, if you choose to opt out, you may not be able to use
            certain aspects of the Services.
          </li>
        </ul>

        <h3 id="othersources">Information collected from other sources</h3>

        <p>
          <em><strong>In Short:</strong> We may collect limited data from public databases, marketing partners, and other
          outside sources.</em>
        </p>

        <p>
          In order to enhance our ability to provide relevant marketing, offers, and services to you and update
          our records, we may obtain information about you from other sources, such as public databases, joint
          marketing partners, affiliate programs, data providers, and from other third parties. This information
          includes mailing addresses, job titles, email addresses, phone numbers, intent data (or user behavior
          data), Internet Protocol (IP) addresses, social media profiles, social media URLs, and custom profiles,
          for purposes of targeted advertising and event promotion.
        </p>
      </section>

      <section id="infouse">
        <h2>2. How do we process your information?</h2>

        <p>
          <em><strong>In Short:</strong> We process your information to provide, improve, and administer our Services,
          communicate with you, for security and fraud prevention, and to comply with law. We may also process
          your information for other purposes with your consent.</em>
        </p>

        <p>
          <strong>We process your personal information for a variety of reasons, depending on how you interact
          with our Services, including:</strong>
        </p>

        <ul>
          <li>
            <strong>To facilitate account creation and authentication and otherwise manage user accounts.</strong>{" "}
            We may process your information so you can create and log in to your account, as well as keep your
            account in working order.
          </li>
          <li>
            <strong>To deliver and facilitate delivery of services to the user.</strong>{" "}
            We may process your information to provide you with the requested service.
          </li>
          <li>
            <strong>To request feedback.</strong>{" "}
            We may process your information when necessary to request feedback and to contact you about your
            use of our Services.
          </li>
        </ul>
      </section>

      <section id="whoshare">
        <h2>3. When and with whom do we share your personal information?</h2>

        <p>
          <em><strong>In Short:</strong> We may share information in specific situations described in this section
          and/or with the following third parties.</em>
        </p>

        <p>We may need to share your personal information in the following situations:</p>

        <ul>
          <li>
            <strong>Business Transfers.</strong>{" "}
            We may share or transfer your information in connection with, or during negotiations of, any merger,
            sale of company assets, financing, or acquisition of all or a portion of our business to another company.
          </li>
        </ul>
      </section>

      <section id="cookies">
        <h2>4. Do we use cookies and other tracking technologies?</h2>

        <p>
          <em><strong>In Short:</strong> We may use cookies and other tracking technologies to collect and store your
          information.</em>
        </p>

        <p>
          We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information
          when you interact with our Services. Some online tracking technologies help us maintain the security of
          our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic
          site functions.
        </p>

        <p>
          We also permit third parties and service providers to use online tracking technologies on our Services
          for analytics and advertising, including to help manage and display advertisements, to tailor
          advertisements to your interests, or to send abandoned shopping cart reminders (depending on your
          communication preferences). The third parties and service providers use their technology to provide
          advertising about products and services tailored to your interests which may appear either on our
          Services or on other websites.
        </p>

        <p>
          To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes
          targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt
          out of these online tracking technologies by submitting a request as described below under section "
          <a href="#uslaws">Do United States residents have specific privacy rights?</a>"
        </p>

        <p>
          Specific information about how we use such technologies and how you can refuse certain cookies is set
          out in our Cookie Notice.
        </p>

        <h3>Google Analytics</h3>
        <p>
          We may share your information with Google Analytics to track and analyze the use of the Services.
          To opt out of being tracked by Google Analytics across the Services, visit{" "}
          <a href="https://tools.google.com/dlpage/gaoptout" rel="noopener noreferrer" target="_blank">
            https://tools.google.com/dlpage/gaoptout
          </a>
          . For more information on the privacy practices of Google, please visit the{" "}
          <a href="https://policies.google.com/privacy" rel="noopener noreferrer" target="_blank">
            Google Privacy &amp; Terms page
          </a>
          .
        </p>
      </section>

      <section id="ai">
        <h2>5. Do we offer artificial intelligence-based products?</h2>

        <p>
          <em><strong>In Short:</strong> We offer products, features, or tools powered by artificial intelligence,
          machine learning, or similar technologies.</em>
        </p>

        <p>
          As part of our Services, we offer products, features, or tools powered by artificial intelligence,
          machine learning, or similar technologies (collectively, "AI Products"). These tools are designed to
          enhance your experience and provide you with innovative solutions. The terms in this Privacy Notice
          govern your use of the AI Products within our Services.
        </p>

        <h3>Use of AI technologies</h3>
        <p>
          We provide the AI Products through third-party service providers ("AI Service Providers"), including
          OpenAI. As outlined in this Privacy Notice, your input, output, and personal information will be shared
          with and processed by these AI Service Providers to enable your use of our AI Products for purposes
          outlined in "
          <a href="#whoshare">When and with whom do we share your personal information?</a>
          ". You must not use the AI Products in any way that violates the terms or policies of any AI Service
          Provider.
        </p>

        <h3>Our AI products</h3>
        <p>Our AI Products are designed for the following functions:</p>
        <ul>
          <li>Text Generation</li>
        </ul>

        <h3>How we process your data using AI</h3>
        <p>
          All personal information processed using our AI Products is handled in line with our Privacy Notice
          and our agreement with third parties. This ensures high security and safeguards your personal
          information throughout the process, giving you peace of mind about your data's safety.
        </p>
      </section>

      <section id="inforetain">
        <h2>6. How long do we keep your information?</h2>

        <p>
          <em><strong>In Short:</strong> We keep your information for as long as necessary to fulfill the purposes
          outlined in this Privacy Notice unless otherwise required by law.</em>
        </p>

        <p>
          We will only keep your personal information for as long as it is necessary for the purposes set out
          in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice will require us keeping your
          personal information for longer than the period of time in which users have an account with us.
        </p>

        <p>
          When we have no ongoing legitimate business need to process your personal information, we will either
          delete or anonymize such information, or, if this is not possible (for example, because your personal
          information has been stored in backup archives), then we will securely store your personal information
          and isolate it from any further processing until deletion is possible.
        </p>
      </section>

      <section id="infosafe">
        <h2>7. How do we keep your information safe?</h2>

        <p>
          <em><strong>In Short:</strong> We aim to protect your personal information through a system of organizational
          and technical security measures.</em>
        </p>

        <p>
          We have implemented appropriate and reasonable technical and organizational security measures designed
          to protect the security of any personal information we process. However, despite our safeguards and
          efforts to secure your information, no electronic transmission over the Internet or information storage
          technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able to defeat our security and
          improperly collect, access, steal, or modify your information. Although we will do our best to protect
          your personal information, transmission of personal information to and from our Services is at your own
          risk. You should only access the Services within a secure environment.
        </p>
      </section>

      <section id="infominors">
        <h2>8. Do we collect information from minors?</h2>

        <p>
          <em><strong>In Short:</strong> We do not knowingly collect data from or market to children under 18 years of
          age.</em>
        </p>

        <p>
          We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do
          we knowingly sell such personal information. By using the Services, you represent that you are at least
          18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of
          the Services. If we learn that personal information from users less than 18 years of age has been
          collected, we will deactivate the account and take reasonable measures to promptly delete such data
          from our records. If you become aware of any data we may have collected from children under age 18,
          please contact us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          .
        </p>
      </section>

      <section id="privacyrights">
        <h2>9. What are your privacy rights?</h2>

        <p>
          <em><strong>In Short:</strong> You may review, change, or terminate your account at any time, depending on
          your country, province, or state of residence.</em>
        </p>

        <p id="withdrawconsent">
          <strong><u>Withdrawing your consent:</u></strong>{" "}
          If we are relying on your consent to process your personal information, which may be express and/or
          implied consent depending on the applicable law, you have the right to withdraw your consent at any
          time. You can withdraw your consent at any time by contacting us by using the contact details provided
          in the section "<a href="#contact">How can you contact us about this notice?</a>" below.
        </p>

        <p>
          However, please note that this will not affect the lawfulness of the processing before its withdrawal
          nor, when applicable law allows, will it affect the processing of your personal information conducted
          in reliance on lawful processing grounds other than consent.
        </p>

        <h3>Account information</h3>

        <p>
          If you would at any time like to review or change the information in your account or terminate your
          account, you can:
        </p>

        <ul>
          <li>Contact us using the contact information provided.</li>
        </ul>

        <p>
          Upon your request to terminate your account, we will deactivate or delete your account and information
          from our active databases. However, we may retain some information in our files to prevent fraud,
          troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with
          applicable legal requirements.
        </p>

        <p>
          If you have questions or comments about your privacy rights, you may email us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          .
        </p>
      </section>

      <section id="DNT">
        <h2>10. Controls for Do-Not-Track features</h2>

        <p>
          Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track
          ("DNT") feature or setting you can activate to signal your privacy preference not to have data about
          your online browsing activities monitored and collected. At this stage, no uniform technology standard
          for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond
          to DNT browser signals or any other mechanism that automatically communicates your choice not to be
          tracked online. If a standard for online tracking is adopted that we must follow in the future, we
          will inform you about that practice in a revised version of this Privacy Notice.
        </p>

        <p>
          California law requires us to let you know how we respond to web browser DNT signals. Because there
          currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not
          respond to them at this time.
        </p>
      </section>

      <section id="uslaws">
        <h2>11. Do United States residents have specific privacy rights?</h2>

        <p>
          <em><strong>In Short:</strong> If you are a resident of California, Colorado, Connecticut, Delaware, Florida,
          Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
          Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and
          receive details about the personal information we maintain about you and how we have processed it,
          correct inaccuracies, get a copy of, or delete your personal information. You may also have the right
          to withdraw your consent to our processing of your personal information. These rights may be limited
          in some circumstances by applicable law. More information is provided below.</em>
        </p>

        <h3>Categories of personal information we collect</h3>

        <p>
          The table below shows the categories of personal information we have collected in the past twelve (12)
          months. The table includes illustrative examples of each category and does not reflect the personal
          information we collect from you. For a comprehensive inventory of all personal information we process,
          please refer to the section "<a href="#infocollect">What information do we collect?</a>"
        </p>

        <div className="booth-legal-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Examples</th>
                <th>Collected</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>A. Identifiers</td>
                <td>
                  Contact details, such as real name, alias, postal address, telephone or mobile contact number,
                  unique personal identifier, online identifier, Internet Protocol address, email address, and
                  account name
                </td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>B. Personal information as defined in the California Customer Records statute</td>
                <td>Name, contact information, education, employment, employment history, and financial information</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>C. Protected classification characteristics under state or federal law</td>
                <td>
                  Gender, age, date of birth, race and ethnicity, national origin, marital status, and other
                  demographic data
                </td>
                <td>No</td>
              </tr>
              <tr>
                <td>D. Commercial information</td>
                <td>Transaction information, purchase history, financial details, and payment information</td>
                <td>No</td>
              </tr>
              <tr>
                <td>E. Biometric information</td>
                <td>Fingerprints and voiceprints</td>
                <td>No</td>
              </tr>
              <tr>
                <td>F. Internet or other similar network activity</td>
                <td>
                  Browsing history, search history, online behavior, interest data, and interactions with our
                  and other websites, applications, systems, and advertisements
                </td>
                <td>No</td>
              </tr>
              <tr>
                <td>G. Geolocation data</td>
                <td>Device location</td>
                <td>No</td>
              </tr>
              <tr>
                <td>H. Audio, electronic, sensory, or similar information</td>
                <td>Images and audio, video or call recordings created in connection with our business activities</td>
                <td>No</td>
              </tr>
              <tr>
                <td>I. Professional or employment-related information</td>
                <td>
                  Business contact details in order to provide you our Services at a business level or job title,
                  work history, and professional qualifications if you apply for a job with us
                </td>
                <td>No</td>
              </tr>
              <tr>
                <td>J. Education Information</td>
                <td>Student records and directory information</td>
                <td>No</td>
              </tr>
              <tr>
                <td>K. Inferences drawn from collected personal information</td>
                <td>
                  Inferences drawn from any of the collected personal information listed above to create a profile
                  or summary about, for example, an individual's preferences and characteristics
                </td>
                <td>No</td>
              </tr>
              <tr>
                <td>L. Sensitive personal Information</td>
                <td></td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          We may also collect other personal information outside of these categories through instances where you
          interact with us in person, online, or by phone or mail in the context of:
        </p>

        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
        </ul>

        <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>

        <ul>
          <li>Category A - As long as the user has an account with us</li>
          <li>Category B - As long as the user has an account with us</li>
        </ul>

        <h3>Sources of personal information</h3>
        <p>
          Learn more about the sources of personal information we collect in "
          <a href="#infocollect">What information do we collect?</a>"
        </p>

        <h3>How we use and share personal information</h3>
        <p>
          Learn more about how we use your personal information in the section, "
          <a href="#infouse">How do we process your information?</a>"
        </p>

        <p><strong>Will your information be shared with anyone else?</strong></p>

        <p>
          We may disclose your personal information with our service providers pursuant to a written contract
          between us and each service provider. Learn more about how we disclose personal information to in
          the section, "<a href="#whoshare">When and with whom do we share your personal information?</a>"
        </p>

        <p>
          We may use your personal information for our own business purposes, such as for undertaking internal
          research for technological development and demonstration. This is not considered to be "selling" of
          your personal information.
        </p>

        <p>
          We have not disclosed, sold, or shared any personal information to third parties for a business or
          commercial purpose in the preceding twelve (12) months. We will not sell or share personal information
          in the future belonging to website visitors, users, and other consumers.
        </p>

        <h3>Your rights</h3>
        <p>
          You have rights under certain US state data protection laws. However, these rights are not absolute,
          and in certain cases, we may decline your request as permitted by law. These rights include:
        </p>

        <ul>
          <li><strong>Right to know</strong> whether or not we are processing your personal data</li>
          <li><strong>Right to access</strong> your personal data</li>
          <li><strong>Right to correct</strong> inaccuracies in your personal data</li>
          <li><strong>Right to request</strong> the deletion of your personal data</li>
          <li><strong>Right to obtain a copy</strong> of the personal data you previously shared with us</li>
          <li><strong>Right to non-discrimination</strong> for exercising your rights</li>
          <li>
            <strong>Right to opt out</strong> of the processing of your personal data if it is used for targeted
            advertising (or sharing as defined under California's privacy law), the sale of personal data, or
            profiling in furtherance of decisions that produce legal or similarly significant effects ("profiling")
          </li>
          <li>
            Depending upon the state where you live, you may also have the following rights:
            <ul>
              <li>
                Right to access the categories of personal data being processed (as permitted by applicable law,
                including the privacy law in Minnesota)
              </li>
              <li>
                Right to obtain a list of the categories of third parties to which we have disclosed personal data
                (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)
              </li>
              <li>
                Right to obtain a list of specific third parties to which we have disclosed personal data (as
                permitted by applicable law, including the privacy law in Minnesota and Oregon)
              </li>
              <li>
                Right to obtain a list of third parties to which we have sold personal data (as permitted by
                applicable law, including the privacy law in Connecticut)
              </li>
              <li>
                Right to review, understand, question, and depending on where you live, correct how personal data
                has been profiled (as permitted by applicable law, including the privacy law in Connecticut and
                Minnesota)
              </li>
              <li>
                Right to limit use and disclosure of sensitive personal data (as permitted by applicable law,
                including the privacy law in California)
              </li>
              <li>
                Right to opt out of the collection of sensitive data and personal data collected through the
                operation of a voice or facial recognition feature (as permitted by applicable law, including
                the privacy law in Florida)
              </li>
            </ul>
          </li>
        </ul>

        <h3>How to exercise your rights</h3>
        <p>
          To exercise these rights, you can contact us by visiting{" "}
          <a rel="noopener noreferrer" target="_blank" href="https://thebooth.gg/contactus">
            https://thebooth.gg/contactus
          </a>
          , or by referring to the contact details at the bottom of this document.
        </p>

        <p>
          Under certain US state data protection laws, you can designate an authorized agent to make a request
          on your behalf. We may deny a request from an authorized agent that does not submit proof that they
          have been validly authorized to act on your behalf in accordance with applicable laws.
        </p>

        <h3>Request verification</h3>
        <p>
          Upon receiving your request, we will need to verify your identity to determine you are the same
          person about whom we have the information in our system. We will only use personal information
          provided in your request to verify your identity or authority to make the request. However, if we
          cannot verify your identity from the information already maintained by us, we may request that you
          provide additional information for the purposes of verifying your identity and for security or
          fraud-prevention purposes.
        </p>

        <p>
          If you submit the request through an authorized agent, we may need to collect additional information
          to verify your identity before processing your request and the agent will need to provide a written
          and signed permission from you to submit such request on your behalf.
        </p>

        <h3>Appeals</h3>
        <p>
          Under certain US state data protection laws, if we decline to take action regarding your request,
          you may appeal our decision by emailing us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          . We will inform you in writing of any action taken or not taken in response to the appeal,
          including a written explanation of the reasons for the decisions. If your appeal is denied, you
          may submit a complaint to your state attorney general.
        </p>

        <h3>California "Shine the Light" law</h3>
        <p>
          California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users
          who are California residents to request and obtain from us, once a year and free of charge,
          information about categories of personal information (if any) we disclosed to third parties for
          direct marketing purposes and the names and addresses of all third parties with which we shared
          personal information in the immediately preceding calendar year. If you are a California resident
          and would like to make such a request, please submit your request in writing to us by using the
          contact details provided in the section "
          <a href="#contact">How can you contact us about this notice?</a>"
        </p>

        <p>United States</p>
      </section>

      <section id="policyupdates">
        <h2>12. Do we make updates to this notice?</h2>

        <p>
          <em><strong>In Short:</strong> Yes, we will update this notice as necessary to stay compliant with relevant
          laws.</em>
        </p>

        <p>
          We may update this Privacy Notice from time to time. The updated version will be indicated by an
          updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy
          Notice, we may notify you either by prominently posting a notice of such changes or by directly sending
          you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we
          are protecting your information.
        </p>
      </section>

      <section id="contact">
        <h2>13. How can you contact us about this notice?</h2>

        <p>
          If you have questions or comments about this notice, you may email us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          {" "}or contact us by post at:
        </p>

        <p>
          The Booth
          <br />
          Seattle, WA
        </p>
      </section>

      <section id="request">
        <h2>14. How can you review, update, or delete the data we collect from you?</h2>

        <p>
          Based on the applicable laws of your country or state of residence in the US, you may have the right
          to request access to the personal information we collect from you, details about how we have processed
          it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw
          your consent to our processing of your personal information. These rights may be limited in some
          circumstances by applicable law. To request to review, update, or delete your personal information,
          please visit:{" "}
          <a rel="noopener noreferrer" target="_blank" href="https://thebooth.gg/contactus">
            https://thebooth.gg/contactus
          </a>
          .
        </p>

        <a
          href="https://app.termly.io/dsar/aaeaf77f-a43f-46b8-be3f-cd83fca6e2a4"
          aria-label="Termly data subject access request"
          style={{ display: "none" }}
        />
      </section>

      <p className="booth-legal-generator">
        This Privacy Policy was created using Termly's{" "}
        <a href="https://termly.io/products/privacy-policy-generator/" target="_blank" rel="noopener noreferrer external">
          Privacy Policy Generator
        </a>
        .
      </p>
    </article>
  );
}

export default PrivacyPolicyContent;
